import React, { useState } from "react";
import "../styles/ResearchSection.css";
import ResearchItem from "./ResearchItem";

const tags = [
  "All",
  "Motion Understanding",
  "Diffusion Features",
  "Bioacoustics",
];

const ResearchSection = () => {
  const [selectedTag, setSelectedTag] = useState("All");

  const isVisible = (tag) => selectedTag === "All" || selectedTag === tag;

  return (
    <div className="research-section">
      <h2>Research</h2>
      <div className="research-filters" aria-label="Filter research by topic">
        {tags.map((tag) => (
          <button
            key={tag}
            type="button"
            className={`research-filter ${
              selectedTag === tag ? "selected" : ""
            }`}
            aria-pressed={selectedTag === tag}
            onClick={() => setSelectedTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>
      <div className="research-list">
        {isVisible("Motion Understanding") && (
          <ResearchItem
            title="What Moves? Localized Motion Representations for Compositional Scene Control"
            image="whatmoves.png"
            video="whatmoves2.mp4"
            authors={[
              { name: "Frank Fundel*", url: "https://ffundel.de/" },
              {
                name: "Malek Ben Alaya*",
                url: "https://www.linkedin.com/in/malek-ben-alaya/",
              },
              {
                name: "Thomas Ressler-Antal*",
                url: "https://www.linkedin.com/in/thomas-ressler-494758133/",
              },
              {
                name: "Stefan Andreas Baumann",
                url: "https://stefan-baumann.eu/",
              },
              { name: "Björn Ommer", url: "https://ommer-lab.com/ommer/" },
            ]}
            journal="ECCV 2026"
            tag="Motion Understanding"
            highlightedAuthor={0}
            description="A promptable framework for object-level motion embeddings that preserve full-scene context for localized motion understanding and transfer."
            arxivUrl="https://arxiv.org/abs/2609.04383"
            projectUrl="https://compvis.github.io/WhatMoves/"
            codeUrl="https://github.com/CompVis/WhatMoves"
            demoUrl="https://huggingface.co/spaces/FrankFundel/WhatMoves"
            highlighted
          />
        )}

        {isVisible("Bioacoustics") && (
          <ResearchItem
            title="Unlabeled Echoes: Pseudo-Labels and Genus-Aware Smoothing for Bat Call Recognition"
            image="unlabeledechoes-thumbnail.png"
            authors={[
              { name: "Frank Fundel", url: "https://ffundel.de/" },
              "Alexandra Howard",
            ]}
            journal="CV4Ecology @ ECCV 2026"
            tag="Bioacoustics"
            highlightedAuthor={0}
            description="A simple pseudo-labeling and genus-aware smoothing approach for improving bat-call recognition from unlabeled recordings."
            projectUrl="https://code4conservation.github.io/UnlabeledEchoes/"
            codeUrl="https://github.com/Code4Conservation/UnlabeledEchoes"
            demoUrl="https://github.com/FrankFundel/OpenEcho"
            arxivUrl="https://arxiv.org/abs/2609.11986"
          />
        )}

        {selectedTag === "All" && (
          <ResearchItem
            title="FlowTouch: View-Invariant Visuo-Tactile Prediction"
            image="FlowTouch.png"
            authors={[
              "Seongjin Bien*",
              "Carlo Kneissl*",
              "Tobias Jülg*",
              { name: "Frank Fundel", url: "https://ffundel.de/" },
              {
                name: "Thomas Ressler-Antal",
                url: "https://www.linkedin.com/in/thomas-ressler-494758133/",
              },
              "Florian Walter",
              { name: "Björn Ommer", url: "https://ommer-lab.com/ommer/" },
              "Gitta Kutyniok",
              "Wolfram Burgard",
            ]}
            journal="IROS 2026"
            highlightedAuthor={3}
            description="A geometry-conditioned generative framework for view-invariant visuo-tactile prediction without requiring exploratory robot motion."
            arxivUrl="https://arxiv.org/abs/2603.08255"
            //codeUrl="https://github.com/flowtouch/flowtouch"
            projectUrl="https://flowtouch.github.io/"
          />
        )}

        {isVisible("Motion Understanding") && (
          <ResearchItem
            title="DisMo: Disentangled Motion Representations for Open-World Motion Transfer"
            image="DisMo.png"
            video="DisMo2.mp4"
            authors={[
              {
                name: "Thomas Ressler-Antal",
                url: "https://www.linkedin.com/in/thomas-ressler-494758133/",
              },
              { name: "Frank Fundel", url: "https://ffundel.de/" },
              {
                name: "Malek Ben Alaya",
                url: "https://www.linkedin.com/in/malek-ben-alaya/",
              },
              {
                name: "Stefan Andreas Baumann",
                url: "https://stefan-baumann.eu/",
              },
              {
                name: "Felix Krause",
                url: "https://www.linkedin.com/in/felixmkrause/",
              },
              {
                name: "Ming Gui",
                url: "https://www.linkedin.com/in/ming-gui-87b76a16b/",
              },
              { name: "Björn Ommer", url: "https://ommer-lab.com/ommer/" },
            ]}
            journal="NeurIPS 2025"
            tag="Motion Understanding"
            highlightedAuthor={1}
            description="A novel framework to learn abstract, appearance-disentangled motion representations for motion transfer and motion understanding."
            arxivUrl="https://arxiv.org/abs/2511.23428"
            codeUrl="https://github.com/CompVis/dismo"
            projectUrl="https://compvis.github.io/DisMo/"
            highlighted
          />
        )}

        {isVisible("Diffusion Features") && (
          <ResearchItem
            title="CleanDIFT: Diffusion Features without Noise"
            image="CleanDIFT.png"
            authors={[
              { name: "Nick Stracke*", url: "https://nickstracke.dev/" },
              {
                name: "Stefan Andreas Baumann*",
                url: "https://stefan-baumann.eu/",
              },
              { name: "Kolja Bauer*", url: "http://koljabauer.com/" },
              { name: "Frank Fundel", url: "https://ffundel.de/" },
              { name: "Björn Ommer", url: "https://ommer-lab.com/ommer/" },
            ]}
            journal="CVPR 2025"
            tag="Diffusion Features"
            highlightedAuthor={3}
            description="A new method to extract noise-free, timestep-independent features by enabling diffusion models to work directly with clean input images."
            arxivUrl="https://arxiv.org/abs/2412.03439"
            codeUrl="https://github.com/CompVis/cleandift"
            projectUrl="https://compvis.github.io/cleandift/"
          />
        )}

        {selectedTag === "All" && (
          <ResearchItem
            title="Diff2Flow: Training Flow Matching Models via Diffusion Model Alignment"
            image="diff2flow.png"
            authors={[
              {
                name: "Johannes Schusterbauer*",
                url: "https://johfischer.com/",
              },
              {
                name: "Ming Gui*",
                url: "https://www.linkedin.com/in/ming-gui-87b76a16b/",
              },
              { name: "Frank Fundel", url: "https://ffundel.de/" },
              { name: "Björn Ommer", url: "https://ommer-lab.com/ommer/" },
            ]}
            journal="CVPR 2025"
            highlightedAuthor={2}
            description="A new method that enables efficient fine-tuning of flow matching models using diffusion priors."
            arxivUrl="https://arxiv.org/abs/2506.02221"
            codeUrl="https://github.com/CompVis/diff2flow"
          />
        )}

        {isVisible("Diffusion Features") && (
          <ResearchItem
            title="DistillDIFT: Distillation of Diffusion Features for Semantic Correspondence"
            image="DistillDIFT.png"
            video="DistillDIFT2.mp4"
            authors={[
              { name: "Frank Fundel", url: "https://ffundel.de/" },
              {
                name: "Johannes Schusterbauer",
                url: "https://johfischer.com/",
              },
              { name: "Vincent Tao Hu", url: "https://taohu.me/" },
              { name: "Björn Ommer", url: "https://ommer-lab.com/ommer/" },
            ]}
            highlightedAuthor={0}
            journal="WACV 2025"
            tag="Diffusion Features"
            description="We demonstrate how to distill two large vision foundation models into a smaller, high-accuracy model with lower computational cost."
            arxivUrl="https://arxiv.org/abs/2412.03512"
            codeUrl="https://github.com/compvis/distilldift"
            projectUrl="https://compvis.github.io/distilldift/"
            highlighted
          />
        )}

        {isVisible("Bioacoustics") && (
          <ResearchItem
            title="BAT: Automatic bat call classification using transformer networks"
            image="BAT.png"
            authors={[
              { name: "Frank Fundel", url: "https://ffundel.de/" },
              {
                name: "Daniel A. Braun",
                url: "https://www.uni-ulm.de/in/neuroinformatik/institut/hidden/d-braun/",
              },
              { name: "Sebastian Gottwald", url: "https://gttwld.de/" },
            ]}
            highlightedAuthor={0}
            journal="Ecological Informatics"
            tag="Bioacoustics"
            description="ConvNet-Transformer hybrid model enables sequence-based bat call classification, surpassing previous single call approaches.."
            arxivUrl="https://arxiv.org/abs/2309.11218"
            codeUrl="https://github.com/FrankFundel/BAT"
          />
        )}
      </div>
    </div>
  );
};

export default ResearchSection;
